import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import { useStore } from '@tanstack/react-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { Servicio } from '@/types/database'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useServicioForm,
  getFieldError,
  CATEGORIAS_SERVICIO_OPTIONS,
  PERIODICIDAD_SERVICIO_LABELS,
  type ServicioFormValues,
  type CategoriaServicio,
  type PeriodicidadServicio,
  precioEditable,
} from './forms'

// Re-export para compatibilidad
export type { ServicioFormValues }

interface ServicioFormProps {
  servicio?: Servicio | null
  onSubmit: (data: ServicioFormValues) => void
  onCancel?: () => void
  isLoading?: boolean
  hideButtons?: boolean
  /** Receives whether the form has unsaved changes. Reports false on unmount. */
  onDirtyChange?: (dirty: boolean) => void
}

/**
 * Mapea un servicio existente a los valores del formulario
 */
function mapServicioToFormValues(servicio: Servicio): Partial<ServicioFormValues> {
  const s = servicio as Servicio & Record<string, unknown>
  return {
    nombre: servicio.nombre ?? '',
    categoria: (s.categoria as CategoriaServicio) ?? 'otros',
    descripcion: (s.descripcion as string) ?? null,
    precio_estimado: (s.precio_estimado as number) ?? null,
    periodicidad: (s.periodicidad as PeriodicidadServicio) ?? 'mensual',
    estado: (s.estado as 'activo' | 'inactivo') ?? 'activo',
    aplica_persona_fisica: (s.aplica_persona_fisica as boolean) ?? true,
    aplica_persona_juridica: (s.aplica_persona_juridica as boolean) ?? true,
    aplica_sucesion_indivisa: (s.aplica_sucesion_indivisa as boolean) ?? true,
  }
}

export const ServicioForm = forwardRef<HTMLFormElement, ServicioFormProps>(
  function ServicioForm({ servicio, onSubmit, onCancel, isLoading, hideButtons = false, onDirtyChange }, ref) {
    const formRef = useRef<HTMLFormElement>(null)
    const { puede } = usePermissions()

    const { form } = useServicioForm({
      defaultValues: servicio ? mapServicioToFormValues(servicio) : undefined,
      onSubmit,
    })

    const isDirty = useStore(form.store, (s) => s.isDirty)
    useEffect(() => {
      onDirtyChange?.(isDirty)
      return () => onDirtyChange?.(false)
    }, [isDirty, onDirtyChange])

    // Exponer el form ref para submit externo
    useImperativeHandle(ref, () => formRef.current!)

    return (
      <form
        ref={formRef}
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-6"
      >
        {/* Nombre */}
        <form.Field name="nombre">
          {(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>Nombre *</Label>
              <Input
                id={field.name}
                placeholder="Ej: Liquidacion IVA, DDJJ Ganancias, Alta Monotributo..."
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {/* Categoria */}
        <form.Field name="categoria">
          {(field) => (
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <Select
                value={field.state.value || ''}
                onValueChange={(value) => field.handleChange(value as CategoriaServicio)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar categoria..." />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIAS_SERVICIO_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {/* Precio Estimado: sin verHonorariosCliente, o si el servicio llegó sin precio, no se edita */}
        {precioEditable(puede.verHonorariosCliente, servicio) && (
          <form.Field name="precio_estimado">
            {(field) => (
              <div className="space-y-2">
                <Label>Precio de Referencia</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    $
                  </span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="pl-7"
                    value={field.state.value ?? ''}
                    onChange={(e) => {
                      const val = e.target.value
                      field.handleChange(val === '' ? null : parseFloat(val))
                    }}
                    onBlur={field.handleBlur}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Precio orientativo para presupuestos. El precio real se define por cliente.
                </p>
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {getFieldError(field.state.meta.errors)}
                  </p>
                )}
              </div>
            )}
          </form.Field>
        )}

        {/* Es Recurrente (Switch que controla periodicidad) */}
        <form.Field name="periodicidad">
          {(field) => {
            const esRecurrente = field.state.value !== 'unico'
            return (
              <>
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label>Servicio Recurrente</Label>
                    <p className="text-xs text-muted-foreground">
                      {esRecurrente
                        ? 'Este servicio aparece para asignar honorarios mensuales'
                        : 'Este servicio es de cobro unico (ej: inscripciones, tramites)'}
                    </p>
                  </div>
                  <Switch
                    checked={esRecurrente}
                    onCheckedChange={(checked) => {
                      field.handleChange(checked ? 'mensual' : 'unico')
                    }}
                  />
                </div>

                {/* Periodicidad (solo si es recurrente) */}
                {esRecurrente && (
                  <div className="space-y-2">
                    <Label>Periodicidad</Label>
                    <Select
                      value={field.state.value || 'mensual'}
                      onValueChange={(value) => field.handleChange(value as PeriodicidadServicio)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar periodicidad..." />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(PERIODICIDAD_SERVICIO_LABELS)
                          .filter(([key]) => key !== 'unico')
                          .map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )
          }}
        </form.Field>

        {/* Aplica a tipo de persona */}
        <div className="space-y-3 rounded-lg border p-4">
          <Label>Aplica a tipo de persona</Label>
          <p className="text-xs text-muted-foreground">
            Selecciona para que tipos de cliente estara disponible este servicio
          </p>
          <div className="flex flex-wrap gap-4 pt-2">
            <form.Field name="aplica_persona_fisica">
              {(field) => (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="aplica_fisica"
                    checked={field.state.value ?? true}
                    onCheckedChange={(checked) => field.handleChange(!!checked)}
                  />
                  <label htmlFor="aplica_fisica" className="text-sm cursor-pointer">
                    Persona Fisica
                  </label>
                </div>
              )}
            </form.Field>

            <form.Field name="aplica_persona_juridica">
              {(field) => (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="aplica_juridica"
                    checked={field.state.value ?? true}
                    onCheckedChange={(checked) => field.handleChange(!!checked)}
                  />
                  <label htmlFor="aplica_juridica" className="text-sm cursor-pointer">
                    Sociedad
                  </label>
                </div>
              )}
            </form.Field>

            <form.Field name="aplica_sucesion_indivisa">
              {(field) => (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="aplica_sucesion"
                    checked={field.state.value ?? true}
                    onCheckedChange={(checked) => field.handleChange(!!checked)}
                  />
                  <label htmlFor="aplica_sucesion" className="text-sm cursor-pointer">
                    Sucesion Indivisa
                  </label>
                </div>
              )}
            </form.Field>
          </div>
        </div>

        {/* Descripcion */}
        <form.Field name="descripcion">
          {(field) => (
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Textarea
                placeholder="Detalles adicionales del servicio (opcional)..."
                rows={2}
                value={field.state.value || ''}
                onChange={(e) => field.handleChange(e.target.value || null)}
                onBlur={field.handleBlur}
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">
                  {getFieldError(field.state.meta.errors)}
                </p>
              )}
            </div>
          )}
        </form.Field>

        {/* Botones - solo si no estan ocultos */}
        {!hideButtons && (
          <div className="flex justify-end gap-4 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Guardando...' : servicio ? 'Actualizar' : 'Crear Servicio'}
            </Button>
          </div>
        )}
      </form>
    )
  }
)
